// ─────────────────────────────────────────────────────────────────
// Talent School Africa — site config for the landing page, dashboards
// and the weekly timetable. Every commercial number and the week's
// schedule live here, so changing a price or a day never means hunting
// through page code.
//
// Pricing and timetable decided by the product owner (Stage 3C, Oct 2026).
// ─────────────────────────────────────────────────────────────────

export const CURRENCY = '₦'; // Nigerian Naira

// Every family starts with the first month free and can cancel any time.
export const FREE_MONTHS = 1;
export const OFFER_SHORT = 'First month free';
export const OFFER_LINE = 'First month free. Cancel anytime.';

// Monthly prices, charged after the free month.
export const PLANS = [
  {
    id: 'scholar',
    name: 'Scholar',
    monthly: 5000,
    children: 'One child',
    note: 'Everything one child needs to learn with Ms. Momo.',
    features: [
      'All 24 chess lessons',
      'Coding as it launches',
      'Ms. Momo voice tutor',
      'Badges and certificates',
      'Parent dashboard',
      'Weekly progress reports', // building next
    ],
  },
  {
    id: 'family',
    name: 'Family',
    popular: true,
    monthly: 7500,
    children: '2–3 children',
    note: 'Everything in Scholar, discounted for families.',
    features: [
      'Everything in Scholar',
      'For 2 to 3 children',
      'One parent dashboard for all',
      'Weekly progress reports', // building next
    ],
  },
];

// The plan that fits a family with `n` children.
export function planForChildren(n) {
  return n >= 2 ? PLANS.find(p => p.id === 'family') : PLANS.find(p => p.id === 'scholar');
}

// Display name for the `subscription` value stored on a parent's user
// profile (AuthContext.registerParent writes 'trial' = the free month).
export const SUBSCRIPTION_LABELS = {
  trial: 'Free first month',
  scholar: 'Scholar',
  family: 'Family',
};

export function formatPrice(n) {
  return CURRENCY + n.toLocaleString('en-NG');
}

// ─────────────────────────────────────────────────────────────────
// WEEKLY TIMETABLE — the single source for which subject runs each day.
// Keys are JavaScript day numbers (Sunday = 0 … Saturday = 6).
// `live: true` means a student can start a session that day; other days
// show `soonTitle` / `soonText` on the student dashboard instead.
// utils/sessionSchedule.js reads this, so changing it here changes what
// the app enforces. Test accounts may still start chess on any day.
// ─────────────────────────────────────────────────────────────────
export const TIMETABLE = {
  1: { day: 'Monday',    short: 'Mon', subject: 'chess',  label: 'Chess',  icon: '♟️', live: true },
  2: { day: 'Tuesday',   short: 'Tue', subject: 'coding', label: 'Coding', icon: '💻', live: false,
       soonTitle: 'Coding starts soon',
       soonText: 'Mr. Patrick is getting the coding lessons ready. Chess is back tomorrow!' },
  3: { day: 'Wednesday', short: 'Wed', subject: 'chess',  label: 'Chess',  icon: '♟️', live: true },
  4: { day: 'Thursday',  short: 'Thu', subject: 'coding', label: 'Coding', icon: '💻', live: false,
       soonTitle: 'Coding starts soon',
       soonText: 'Mr. Patrick is getting the coding lessons ready. Chess is back tomorrow!' },
  5: { day: 'Friday',    short: 'Fri', subject: 'chess',  label: 'Chess',  icon: '♟️', live: true },
  6: { day: 'Saturday',  short: 'Sat', subject: 'recap',  label: 'Weekly Recap', icon: '🏆', live: false,
       detail: 'Chess and Coding: quiz, challenges and test',
       soonTitle: 'Weekly Recap coming soon',
       soonText: 'Soon Saturdays bring a quiz, challenges and a test on your week.' },
  0: { day: 'Sunday',    short: 'Sun', subject: 'rest',   label: 'Rest day', icon: '😊', live: false,
       soonTitle: 'Today is your rest day',
       soonText: 'Rest and play today. Chess is back on Monday!' },
};

// Display order, Monday first.
export const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0];

// ─────────────────────────────────────────────────────────────────
// TESTIMONIALS — OFF by default. Do not switch on until every entry is
// a real, consented quote from a real parent. The entries below are
// PLACEHOLDERS ONLY: they are not real people and must never ship.
// ─────────────────────────────────────────────────────────────────
export const SHOW_TESTIMONIALS = false;

export const TESTIMONIALS = [
  { placeholder: true, quote: 'PLACEHOLDER: replace with a real parent quote.', name: 'Placeholder Parent A', detail: 'Parent of a child aged 6–8, City' },
  { placeholder: true, quote: 'PLACEHOLDER: replace with a real parent quote.', name: 'Placeholder Parent B', detail: 'Parent of a child aged 9–12, City' },
  { placeholder: true, quote: 'PLACEHOLDER: replace with a real parent quote.', name: 'Placeholder Parent C', detail: 'Parent of a child aged 13–15, City' },
];
